import { useThreadsStore } from "@circle-react/hooks/chatsV2/store";
import { useRailbar } from "@circle-react/hooks/chatsV2/useRailbar";
import type { ChatRoomMessage } from "@circle-react/types/Chat/ChatRoomMessage";

interface ReactionParams {
  communityMemberId: number;
  emoji: string;
  reactionableId: number;
}

interface WebsocketReactionData {
  event: "createReaction" | "destroyReaction";
  json_message: {
    reactionable_type: string;
    community_member_id: number;
    emoji: string;
    reactionable_id: number;
  };
}

export const useChatThreadReaction = () => {
  const { parentMessage, updateParentMessage } = useRailbar();
  const { chatMessages, setChatMessages, scrollInfo, setScrollInfo } =
    useThreadsStore();

  const updateReactionInMessage = (
    message: ChatRoomMessage,
    { communityMemberId, emoji }: Omit<ReactionParams, "reactionableId">,
    isAdding: boolean,
  ): void => {
    const existingReactionIndex = message.reactions.findIndex(
      reaction => reaction.emoji === emoji,
    );

    if (existingReactionIndex === -1) {
      if (isAdding) {
        message.reactions.push({
          emoji,
          community_member_ids: [communityMemberId],
          count: 1,
        });
      }
      return;
    }

    const reaction = message.reactions[existingReactionIndex];
    const hasReaction =
      reaction.community_member_ids.includes(communityMemberId);

    if (isAdding && hasReaction) return;
    if (!isAdding && !hasReaction) return;

    if (isAdding) {
      reaction.community_member_ids.push(communityMemberId);
      reaction.count += 1;
    } else {
      if (reaction.count === 1) {
        message.reactions.splice(existingReactionIndex, 1);
      } else {
        reaction.community_member_ids = reaction.community_member_ids.filter(
          id => id !== communityMemberId,
        );
        reaction.count -= 1;
      }
    }
  };

  const createReactionFromThread = ({
    communityMemberId,
    emoji,
    reactionableId,
  }: ReactionParams): void => {
    if (parentMessage?.id === reactionableId) {
      const updatedParentMessage = structuredClone(parentMessage);
      updateReactionInMessage(
        updatedParentMessage,
        { communityMemberId, emoji },
        true,
      );
      updateParentMessage(updatedParentMessage);
      return;
    }

    const updatedChatMessages = structuredClone(chatMessages);
    const chatMessageIndex = updatedChatMessages.findIndex(
      (chatMessage: ChatRoomMessage) => chatMessage.id === reactionableId,
    );

    if (chatMessageIndex === -1) return;

    updateReactionInMessage(
      updatedChatMessages[chatMessageIndex],
      { communityMemberId, emoji },
      true,
    );

    setScrollInfo({
      ...scrollInfo,
      action: "noScroll",
    });
    setChatMessages(updatedChatMessages);
  };

  const removeReactionFromThread = ({
    communityMemberId,
    emoji,
    reactionableId,
  }: ReactionParams): void => {
    if (parentMessage?.id === reactionableId) {
      const updatedParentMessage = structuredClone(parentMessage);
      updateReactionInMessage(
        updatedParentMessage,
        { communityMemberId, emoji },
        false,
      );
      updateParentMessage(updatedParentMessage);
      return;
    }

    const updatedChatMessages = structuredClone(chatMessages);
    const chatMessageIndex = updatedChatMessages.findIndex(
      (chatMessage: ChatRoomMessage) => chatMessage.id === reactionableId,
    );

    if (chatMessageIndex === -1) return;

    updateReactionInMessage(
      updatedChatMessages[chatMessageIndex],
      { communityMemberId, emoji },
      false,
    );

    setScrollInfo({
      ...scrollInfo,
      action: "noScroll",
    });
    setChatMessages(updatedChatMessages);
  };

  const handleReactionFromWebsocket = (data: WebsocketReactionData): void => {
    if (data.json_message.reactionable_type !== "ChatRoomMessage") return;

    const {
      community_member_id: communityMemberId,
      emoji,
      reactionable_id: reactionableId,
    } = data.json_message;

    const params = {
      communityMemberId,
      emoji,
      reactionableId,
    };

    if (data.event === "createReaction") {
      createReactionFromThread(params);
    } else if (data.event === "destroyReaction") {
      removeReactionFromThread(params);
    }
  };

  return {
    createReactionFromThread,
    removeReactionFromThread,
    handleReactionFromWebsocket,
  };
};
